import React from "react";
import { useState, useEffect } from "react";
import { Calendar, User, Tag, Clock, ArrowLeft } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";

const UserBlogDetails = () => {
  const [getBlog, setGetBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/blogs/blog-by-id/${id}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch blog");
      }

      const data = await response.json();
      setGetBlog(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full mx-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded-lg mb-4"></div>
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full mx-4 text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Oops! Something went wrong
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchBlog}
            className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!getBlog) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">Blog not found</h2>
          <p className="text-gray-600 mt-2">
            The blog post you're looking for doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="w-full mx-auto px-4 py-4">
          <button className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors group">
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Blogs</span>
          </button>
        </div>
      </div> */}

      <div className="w-full mx-auto px-4 py-8">
        <article className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Hero Image */}
          {getBlog.images && getBlog.images.length > 0 && (
            <div className="relative h-80 md:h-96 overflow-hidden">
              <img
                src={getBlog.images[0]}
                alt={getBlog.title}
                className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>

              {/* Status Badge */}
              <div className="absolute top-6 right-6">
                <span
                  className={`px-4 py-2 rounded-full text-sm font-semibold capitalize ${
                    getBlog.status === "published"
                      ? "bg-green-100 text-green-800 border border-green-200"
                      : "bg-yellow-100 text-yellow-800 border border-yellow-200"
                  }`}
                >
                  {getBlog.status}
                </span>
              </div>
            </div>
          )}

          <div className="p-8 md:p-12">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent leading-tight mb-6">
              {getBlog.title}
            </h1>

            <div className="flex flex-wrap gap-6 mb-8 text-gray-600">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-indigo-500" />
                <span className="font-medium">{getBlog.author}</span>
              </div>

              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-500" />
                <span>{formatDate(getBlog.publishedDate)}</span>
              </div>

              {getBlog.updatedAt !== getBlog.createdAt && (
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-indigo-500" />
                  <span>Updated {formatDate(getBlog.updatedAt)}</span>
                </div>
              )}
            </div>

            {getBlog.tags && getBlog.tags.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="w-5 h-5 text-indigo-500" />
                  <span className="font-semibold text-gray-700">Tags</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {getBlog.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-700 rounded-full text-sm font-medium border border-indigo-100 hover:from-indigo-100 hover:to-blue-100 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="prose prose-lg max-w-none">
              <div className="text-gray-700 leading-relaxed text-lg border-l-4 border-indigo-500 pl-6 bg-gradient-to-r from-indigo-50/50 to-transparent p-6 rounded-r-lg">
                {getBlog.content}
              </div>
            </div>

            {getBlog.images && getBlog.images.length > 1 && (
              <div className="mt-12">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">
                  More Images
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {getBlog.images.slice(1).map((image, index) => (
                    <div
                      key={index}
                      className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <img
                        src={image}
                        alt={`${getBlog.title} - Image ${index + 2}`}
                        className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>

        <div className="mt-12 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-3xl p-8 text-center text-white shadow-2xl">
          <h3 className="text-2xl font-bold mb-4">Enjoyed this article?</h3>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              className="border-2 border-white text-white px-8 py-3 rounded-xl font-semibold hover:bg-white hover:text-indigo-600 transition-colors"
              onClick={() => {
                navigate("/blogs");
              }}
            >
              Read More Blogs
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserBlogDetails;
